import React, { useState, useEffect } from "react";
import {
  Input,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Sheet,
  Container,
  IconButton
} from "@mui/joy";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  useOnboardingStore,
  useUserStore,
  type ContactRole
} from "./../../../../shared/store/index";
import styles from "./Home.module.scss";
import axios from "axios";
import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut
} from "firebase/auth";
import { auth } from "../../../../firebase.config";
import { SERVER_BASE_URL } from "../../../../shared/utils/helpers";
import { useCheckExpiration } from "../../../../shared/api/onboarding-auth.hooks";

// Use environment variable for API base URL (CWE-547 fix)
// Falls back to localhost for local development if not configured
const ONBOARDING_API_BASE = SERVER_BASE_URL;

interface User {
  email: string;
  displayName: string;
  uuid: string;
  roles?: string[];
}

interface OnboardingData {
  status: string;
  tenantId?: string;
  clientName?: string;
  onboardingData?: Record<string, unknown>;
}

interface LoginFormProps {
  onLoginSuccess: (
    user: User,
    token: string,
    onboarding?: OnboardingData
  ) => void;
  /** Called before Firebase auth to prevent transient UI states */
  onLoginStarted?: () => void;
  /** Called when login fails to allow parent to reset loading states */
  onLoginFailed?: () => void;
}

// Capture UUID from initial URL or sessionStorage before router/auth hooks process it
// This MUST happen at module load time to capture the values before useAuthState clears sessionStorage
const getInitialUuid = () => {
  if (typeof window !== "undefined") {
    // First try URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const urlUuid = urlParams.get("uuid");
    if (urlUuid) {
      return urlUuid;
    }

    // Fallback to sessionStorage (for returning users who visit /login directly)
    // This must be captured here before useAuthState clears sessionStorage
    try {
      const onboardingUserStr = sessionStorage.getItem("onboardingUser");
      if (onboardingUserStr) {
        const onboardingUser = JSON.parse(onboardingUserStr);
        if (onboardingUser?.uuid) {
          return onboardingUser.uuid;
        }
      }
    } catch {
      // Ignore parsing errors
    }
  }
  return "";
};

// Store the initial UUID globally to avoid losing it
const INITIAL_UUID = getInitialUuid();

// Password policy constants (must match backend: onboarding-auth.controller.ts)
const PASSWORD_MIN_LENGTH = 12;

// Common weak passwords to block (subset matching backend list)
const COMMON_PASSWORDS = new Set([
  "password",
  "password1",
  "password12",
  "password123",
  "password1234",
  "12345678",
  "123456789",
  "1234567890",
  "123456789012",
  "qwerty",
  "qwertyuiop",
  "qwerty123",
  "letmein",
  "welcome",
  "welcome1",
  "welcome123",
  "admin",
  "admin123",
  "administrator",
  "changeme",
  "changeme123",
  "iloveyou",
  "monkey",
  "dragon",
  "master",
  "sunshine",
  "princess",
  "football",
  "baseball",
  "abc123",
  "abcd1234",
  "trustno1",
  "passw0rd",
  "p@ssword",
  "p@ssw0rd"
]);

/**
 * Validates password meets complexity requirements
 * Must match backend validation in onboarding-auth.controller.ts
 * Returns null if valid, or an error message if invalid
 */
const validatePasswordComplexity = (password: string): string | null => {
  if (!password || password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`;
  }

  // Check for common passwords (case-insensitive)
  if (COMMON_PASSWORDS.has(password.toLowerCase())) {
    return "Password is too common. Please choose a stronger password";
  }

  // Check for character diversity
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password);

  const missingRequirements: string[] = [];

  if (!hasUppercase) {
    missingRequirements.push("uppercase letter");
  }
  if (!hasLowercase) {
    missingRequirements.push("lowercase letter");
  }
  if (!hasNumber) {
    missingRequirements.push("number");
  }
  if (!hasSpecial) {
    missingRequirements.push("special character (!@#$%^&*...)");
  }

  if (missingRequirements.length > 0) {
    return `Password must contain at least one: ${missingRequirements.join(", ")}`;
  }

  return null; // Password is valid
};

// Helper to get persisted password reset state (survives component remount)
const getPersistedPasswordResetState = () => {
  try {
    const stored = sessionStorage.getItem("passwordResetState");
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
};

// Helper to get expired message from sessionStorage (set by onboarding-root when redirecting)
// This is read synchronously on component initialization to avoid flash of login form
const getInitialExpiredState = (): { isExpired: boolean; message: string } => {
  if (typeof window !== "undefined") {
    const storedExpiredMessage = sessionStorage.getItem(
      "onboardingExpiredMessage"
    );
    if (storedExpiredMessage) {
      // Clear the message so it doesn't persist across page refreshes
      sessionStorage.removeItem("onboardingExpiredMessage");
      return { isExpired: true, message: storedExpiredMessage };
    }
  }
  return { isExpired: false, message: "" };
};

// Capture initial expired state before component renders
const INITIAL_EXPIRED_STATE = getInitialExpiredState();

const EmailPasswordLogin: React.FC<LoginFormProps> = ({
  onLoginSuccess,
  onLoginStarted,
  onLoginFailed
}) => {
  // Check for persisted password reset state on initial render
  // Note: We intentionally do NOT store or restore the password for security reasons
  const persistedState = getPersistedPasswordResetState();

  const [email, setEmail] = useState(persistedState?.email || "");
  // Password is never restored from storage - user must re-enter for security
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(
    persistedState
      ? "For security reasons, you must change your temporary password before continuing. Please re-enter your temporary password."
      : ""
  );
  const [isFirstTime, setIsFirstTime] = useState(!!persistedState);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [uuid, setUuid] = useState(persistedState?.uuid || INITIAL_UUID);

  // Track if link is expired - shows only message, hides login form
  // Initialize from sessionStorage synchronously to avoid flash of login form
  const [isLinkExpired, setIsLinkExpired] = useState(
    INITIAL_EXPIRED_STATE.isExpired
  );
  const [expiredMessage, setExpiredMessage] = useState(
    INITIAL_EXPIRED_STATE.message
  );

  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check sessionStorage for expired message on mount (handles SPA navigation after login error)
  // Also extract UUID from INITIAL_UUID (captured at module load from URL or sessionStorage)
  useEffect(() => {
    // Check sessionStorage for expired message (set during login error handling)
    // This is needed because INITIAL_EXPIRED_STATE is computed at module load time,
    // but SPA navigation doesn't reload modules, so we need to check dynamically here
    const storedExpiredMessage = sessionStorage.getItem(
      "onboardingExpiredMessage"
    );
    if (storedExpiredMessage) {
      sessionStorage.removeItem("onboardingExpiredMessage");
      setIsLinkExpired(true);
      setExpiredMessage(storedExpiredMessage);
      return;
    }

    // Extract UUID from INITIAL_UUID or URL fallback
    const extractUuid = () => {
      if (INITIAL_UUID) {
        return INITIAL_UUID;
      }
      // Fallback: Try parsing from the full URL
      const fullUrl = window.location.href;
      const uuidMatch = fullUrl.match(/[?&]uuid=([^&]+)/);
      if (uuidMatch) {
        return uuidMatch[1];
      }
      return "";
    };

    const extractedUuid = extractUuid();
    setUuid(extractedUuid);

    // Save the uuid to onboarding store
    const { setUuid: setStoreUuid } = useOnboardingStore.getState();
    setStoreUuid(extractedUuid);
  }, []);

  // Use React Query to check expiration status (per CLAUDE.md guidelines)
  // Only runs if we have a UUID and not already marked as expired from sessionStorage
  const shouldCheckExpiration =
    !!uuid && !isLinkExpired && !INITIAL_EXPIRED_STATE.isExpired;
  const {
    data: expirationData,
    isLoading: isCheckingExpiration,
    isError: isExpirationCheckError
  } = useCheckExpiration(uuid, { enabled: shouldCheckExpiration });

  // If expiration check errors out, allow login attempt (login will validate UUID)
  // Also skip loading if there's no UUID (e.g., after logout)
  const showLoadingState =
    isCheckingExpiration && !isExpirationCheckError && !!uuid;

  // Handle expiration check result from React Query
  useEffect(() => {
    if (expirationData?.expired === true) {
      setIsLinkExpired(true);
      setExpiredMessage(
        expirationData.message ||
          "This onboarding link has expired. Please contact your Rooster point of contact for a new link."
      );
    }
  }, [expirationData]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Notify parent that login is starting - prevents "Access Restricted" flash
    // This must be called BEFORE Firebase auth to cover the state transition gap
    onLoginStarted?.();

    try {
      // Step 1: Authenticate with Firebase using email/password
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Step 2: Get the ID token from Firebase
      const idToken = await userCredential.user.getIdToken();

      // Step 3: Send the ID token to the backend for verification and custom token generation
      const response = await axios.post(
        `${ONBOARDING_API_BASE}/onboarding-auth/login`,
        {
          idToken,
          uuid: uuid || "" // Send empty string if no UUID
        }
      );

      if (response.data.success) {
        // Get UUID from response (may come from custom claims if different from URL)
        const returnedUuid = response.data.user.uuid;
        const customToken = response.data.token;

        // Check if user needs to change their password (first-time login security)
        if (response.data.passwordNeedsReset === true) {
          // Persist state to sessionStorage before signing out
          // SECURITY: We intentionally do NOT store the password in sessionStorage
          // to prevent exposure via XSS, browser extensions, or physical access.
          // User will need to re-enter their temporary password.
          sessionStorage.setItem(
            "passwordResetState",
            JSON.stringify({ email, uuid, passwordResetRequired: true })
          );

          // Sign out of Firebase to prevent useAuthState from hiding the login form
          await signOut(auth);

          // Note: The component will remount after signOut triggers navigation,
          // but the persisted state will restore isFirstTime=true and show the password reset form
          return;
        }

        // Step 4: Sign in with the custom token to establish Firebase session with correct claims
        // This is necessary because the custom token contains provisioning claims (uuid, provisioningUser)
        // that need to be available for subsequent API calls
        const userCredential = await signInWithCustomToken(auth, customToken);

        // Step 5: Extract roles from Firebase custom claims
        // These are set during user creation and used for role-based navigation
        const idTokenResult = await userCredential.user.getIdTokenResult(true);
        const claims = idTokenResult.claims;
        const userRoles = (claims.roles as string[]) || [];

        // Update local state and store with the UUID from backend
        if (returnedUuid && returnedUuid !== uuid) {
          setUuid(returnedUuid);
          const { setUuid: setStoreUuid } = useOnboardingStore.getState();
          setStoreUuid(returnedUuid);
        }

        // Include roles in the user object for session storage
        const userWithRoles: User = {
          ...response.data.user,
          roles: userRoles
        };

        // CRITICAL: Store roles in Zustand store for role-based navigation
        // This allows components like domain.tsx to check user roles via useRoleBasedNavigation hook
        // Without this, the roles array would be empty and role checks would fail
        useUserStore.getState().setRoles(userRoles as ContactRole[]);

        onLoginSuccess(
          userWithRoles,
          response.data.token,
          response.data.onboarding
        );
        // Navigation will be handled by Home.tsx based on onboarding status
      }
    } catch (err: unknown) {
      console.error("Login error:", err);

      // Handle backend API errors first (axios errors have response property)
      if (axios.isAxiosError(err) && err.response) {
        const status = err.response.status;
        const message = err.response.data?.message || "";

        if (status === 401) {
          // Check if the error indicates an expired link
          if (message.toLowerCase().includes("expired")) {
            // Store the expired message in sessionStorage BEFORE signing out
            // This is necessary because signOut triggers useAuthState which redirects to /login
            // and causes the component to remount, losing the state.
            // The INITIAL_EXPIRED_STATE will capture this on remount.
            sessionStorage.setItem("onboardingExpiredMessage", message);

            // Sign out of Firebase
            // (Firebase auth succeeded but backend rejected due to expiration)
            try {
              await signOut(auth);
            } catch {
              // Ignore signOut errors - the redirect will show the expired message
            }

            // Also set local state in case the component doesn't remount
            setIsLinkExpired(true);
            setExpiredMessage(message);
            return;
          }
          // Use the backend's message for other 401 errors
          setError(
            message ||
              "Invalid email or password. Please check your credentials."
          );
        } else if (status === 429) {
          setError("Too many attempts. Please try again later.");
        } else {
          setError(message || "Login failed. Please try again.");
        }
        return;
      }

      // Handle Firebase authentication errors
      const firebaseError = err as { code?: string; message?: string };
      if (firebaseError.code) {
        switch (firebaseError.code) {
          case "auth/invalid-credential":
          case "auth/wrong-password":
            setError(
              "Invalid email or password. Please check your credentials."
            );
            break;
          case "auth/user-not-found":
            // User not found typically means the onboarding link is invalid or expired
            // and the associated Firebase account was deleted during cleanup
            setIsLinkExpired(true);
            setExpiredMessage(
              "This onboarding link is no longer valid. The account may have been removed or the link has expired. Please contact your Rooster point of contact for a new link."
            );
            setLoading(false); // Stop the loading spinner before showing expired UI
            onLoginFailed?.(); // Notify parent to reset its loading state
            return;
          case "auth/too-many-requests":
            setError("Too many failed attempts. Please try again later.");
            break;
          case "auth/user-disabled":
            setError("This account has been disabled. Please contact support.");
            break;
          default:
            setError(`Authentication failed: ${firebaseError.message}`);
        }
        return;
      }

      // Generic error fallback
      setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setError("New passwords don't match");
      return;
    }

    // Validate password complexity (must match backend requirements)
    const passwordError = validatePasswordComplexity(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    setError("");

    // Notify parent that login is starting - prevents "Access Restricted" flash
    onLoginStarted?.();

    try {
      // Step 1: First authenticate with Firebase to verify current password
      await signInWithEmailAndPassword(auth, email, password);

      // Step 2: Call backend to change password (backend will update Firebase)
      const response = await axios.post(
        `${ONBOARDING_API_BASE}/onboarding-auth/change-password`,
        {
          email,
          currentPassword: password,
          newPassword,
          uuid
        }
      );

      if (response.data.success) {
        // Step 3: Re-authenticate with the new password
        const newUserCredential = await signInWithEmailAndPassword(
          auth,
          email,
          newPassword
        );
        const newIdToken = await newUserCredential.user.getIdToken();

        // Step 4: Send the new ID token to backend
        const loginResponse = await axios.post(
          `${ONBOARDING_API_BASE}/onboarding-auth/login`,
          {
            idToken: newIdToken,
            uuid
          }
        );

        if (loginResponse.data.success) {
          // Clear the password reset state from sessionStorage
          sessionStorage.removeItem("passwordResetState");

          // Get UUID from response (may come from custom claims)
          const returnedUuid = loginResponse.data.user.uuid;
          const customToken = loginResponse.data.token;

          // Sign in with the custom token to establish Firebase session
          await signInWithCustomToken(auth, customToken);

          // Update local state and store with the UUID from backend
          if (returnedUuid && returnedUuid !== uuid) {
            setUuid(returnedUuid);
            const { setUuid: setStoreUuid } = useOnboardingStore.getState();
            setStoreUuid(returnedUuid);
          }

          onLoginSuccess(
            loginResponse.data.user,
            loginResponse.data.token,
            loginResponse.data.onboarding
          );
          // Navigation will be handled by Home.tsx based on onboarding status
        }
      }
    } catch (err: unknown) {
      console.error("Password change error:", err);

      // Handle Firebase authentication errors
      const firebaseError = err as { code?: string; message?: string };
      if (firebaseError.code) {
        switch (firebaseError.code) {
          case "auth/invalid-credential":
          case "auth/wrong-password":
            setError("Current password is incorrect.");
            break;
          case "auth/too-many-requests":
            setError("Too many failed attempts. Please try again later.");
            break;
          default:
            setError(`Password change failed: ${firebaseError.message}`);
        }
        return;
      }

      setError("Password change failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        px: { xs: 1, sm: 2 },
        width: "100%"
      }}
    >
      <Sheet
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mt: { xs: 2, sm: 4, md: 8 },
          borderRadius: "md",
          backgroundColor: "background.surface",
          boxShadow: "lg",
          border: "1px solid",
          borderColor: "divider",
          width: "100%",
          maxWidth: { xs: "100%", sm: "400px", md: "450px" },
          mx: "auto",
          // Optimize for short laptop screens
          "@media (max-height: 650px)": {
            p: 1.5,
            mt: 0.5,
            maxWidth: "380px"
          }
        }}
      >
        <Box
          textAlign="center"
          mb={{ xs: 2, sm: 3 }}
          sx={{
            "@media (max-height: 650px)": {
              mb: 1
            }
          }}
        >
          <div className={styles.home_logo} />
          <Typography
            level="h2"
            component="h1"
            mb={1}
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
              "@media (max-height: 650px)": {
                fontSize: "1.125rem",
                mb: 0.5
              }
            }}
          >
            Rooster Onboarding
          </Typography>
        </Box>

        {/* Show loading indicator while checking expiration status */}
        {showLoadingState ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4
            }}
          >
            <CircularProgress size="md" />
            <Typography level="body-sm" sx={{ mt: 2, color: "text.secondary" }}>
              Checking onboarding status...
            </Typography>
          </Box>
        ) : isLinkExpired ? (
          /* Show expired link message without login form */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              py: { xs: 2, sm: 3 }
            }}
          >
            <Alert
              color="warning"
              variant="soft"
              sx={{
                mb: 3,
                width: "100%",
                fontSize: { xs: "0.875rem", sm: "1rem" },
                "& .MuiAlert-message": {
                  width: "100%"
                }
              }}
            >
              <Typography
                level="title-md"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: "warning.800"
                }}
              >
                Onboarding Link Expired
              </Typography>
              <Typography level="body-sm" sx={{ color: "warning.700" }}>
                {expiredMessage}
              </Typography>
            </Alert>

            <Box
              sx={{
                p: { xs: 2, sm: 3 },
                backgroundColor: "neutral.50",
                borderRadius: "md",
                border: "1px solid",
                borderColor: "neutral.200",
                width: "100%"
              }}
            >
              <Typography
                level="title-sm"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  color: "text.primary"
                }}
              >
                What to do next
              </Typography>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                Please contact the Rooster point of contact in your organization
                to request a new onboarding link.
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            {error && (
              <Alert
                id="login-error"
                color="danger"
                role="alert"
                sx={{
                  mb: 2,
                  fontSize: { xs: "0.875rem", sm: "1rem" }
                }}
              >
                {error}
              </Alert>
            )}

            {!isFirstTime ? (
              <Box component="form" onSubmit={handleLogin}>
                <Input
                  fullWidth
                  placeholder="Email"
                  aria-label="Email"
                  aria-describedby={error ? "login-error" : undefined}
                  autoComplete="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{
                    mb: { xs: 1.5, sm: 2 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    "@media (max-height: 650px)": {
                      mb: 1,
                      fontSize: "0.8rem"
                    }
                  }}
                  required
                />
                <Input
                  fullWidth
                  placeholder="Password"
                  aria-label="Password"
                  aria-describedby={error ? "login-error" : undefined}
                  autoComplete="current-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  endDecorator={
                    <IconButton
                      variant="plain"
                      color="neutral"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      sx={{ mr: -0.5 }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  }
                  sx={{
                    mb: { xs: 1.5, sm: 2 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    "@media (max-height: 650px)": {
                      mb: 1,
                      fontSize: "0.8rem"
                    }
                  }}
                  required
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="solid"
                  sx={{
                    mt: { xs: 2, sm: 3 },
                    mb: 2,
                    py: { xs: 1, sm: 1.5 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    "@media (max-height: 650px)": {
                      mt: 1.5,
                      mb: 1.5,
                      py: 0.75,
                      fontSize: "0.8rem"
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size="sm" /> : "Sign In"}
                </Button>

                <Button
                  type="button"
                  fullWidth
                  variant="plain"
                  onClick={() => setIsFirstTime(true)}
                  sx={{
                    mt: 1,
                    py: { xs: 0.75, sm: 1 },
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    "@media (max-height: 650px)": {
                      mt: 0.5,
                      py: 0.5,
                      fontSize: "0.7rem"
                    }
                  }}
                >
                  Forgot password?
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={handlePasswordChange}>
                <Input
                  fullWidth
                  placeholder="Email"
                  aria-label="Email"
                  aria-describedby={error ? "login-error" : undefined}
                  autoComplete="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{
                    mb: { xs: 1.5, sm: 2 },
                    fontSize: { xs: "0.875rem", sm: "1rem" }
                  }}
                  required
                />
                <Input
                  fullWidth
                  placeholder="Current Password"
                  aria-label="Current Password"
                  aria-describedby={error ? "login-error" : undefined}
                  autoComplete="current-password"
                  type={showCurrentPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  endDecorator={
                    <IconButton
                      variant="plain"
                      color="neutral"
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                      aria-label={
                        showCurrentPassword
                          ? "Hide current password"
                          : "Show current password"
                      }
                      sx={{ mr: -0.5 }}
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  }
                  sx={{
                    mb: { xs: 1.5, sm: 2 },
                    fontSize: { xs: "0.875rem", sm: "1rem" }
                  }}
                  required
                />
                <Input
                  fullWidth
                  placeholder="New Password"
                  aria-label="New Password"
                  aria-describedby={error ? "login-error" : undefined}
                  autoComplete="new-password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  endDecorator={
                    <IconButton
                      variant="plain"
                      color="neutral"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      aria-label={
                        showNewPassword
                          ? "Hide new password"
                          : "Show new password"
                      }
                      sx={{ mr: -0.5 }}
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  }
                  sx={{
                    mb: { xs: 1.5, sm: 2 },
                    fontSize: { xs: "0.875rem", sm: "1rem" }
                  }}
                  required
                />
                <Input
                  fullWidth
                  placeholder="Confirm New Password"
                  aria-label="Confirm New Password"
                  aria-describedby={error ? "login-error" : undefined}
                  autoComplete="new-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  endDecorator={
                    <IconButton
                      variant="plain"
                      color="neutral"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      aria-label={
                        showConfirmPassword
                          ? "Hide confirm password"
                          : "Show confirm password"
                      }
                      sx={{ mr: -0.5 }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  }
                  sx={{
                    mb: { xs: 1.5, sm: 2 },
                    fontSize: { xs: "0.875rem", sm: "1rem" }
                  }}
                  required
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="solid"
                  sx={{
                    mt: { xs: 2, sm: 3 },
                    mb: 2,
                    py: { xs: 1, sm: 1.5 },
                    fontSize: { xs: "0.875rem", sm: "1rem" }
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size="sm" />
                  ) : (
                    "Update Password & Sign In"
                  )}
                </Button>

                <Button
                  fullWidth
                  variant="plain"
                  onClick={() => {
                    sessionStorage.removeItem("passwordResetState");
                    setIsFirstTime(false);
                    setError("");
                  }}
                  sx={{
                    mt: 1,
                    py: { xs: 0.75, sm: 1 },
                    fontSize: { xs: "0.75rem", sm: "0.875rem" }
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            )}
          </>
        )}
      </Sheet>
    </Container>
  );
};

export default EmailPasswordLogin;
